'use strict'

const BasUtil = require('@basalte/bas-util')

angular
  .module('basalteApp')
  .controller('securityCtrl', [
    '$rootScope',
    '$scope',
    'SECURITY',
    'ICONS',
    'BasSecurityAlarmTile',
    'BasSecurity',
    'BasUtilities',
    'Logger',
    SecurityCtrl
  ])

/**
 * @param $rootScope
 * @param $scope
 * @param SECURITY
 * @param ICONS
 * @param {BasSecurityAlarmTile} BasSecurityAlarmTile
 * @param {BasSecurity} BasSecurity
 * @param {BasUtilities} BasUtilities
 * @param {Logger} Logger
 */

function SecurityCtrl (
  $rootScope,
  $scope,
  SECURITY,
  ICONS,
  BasSecurityAlarmTile,
  BasSecurity,
  BasUtilities,
  Logger
) {
  const security = this
  let _listeners = []

  /**
   * @type {TBasSecurityState}
   * @typedef {Object} TAlarmTile
   * @property {string} name
   * @property {string} uiIcon
   * @property {string} uuid
   */
  security.state = BasSecurity.get()

  security.showInputModal = showInputModal
  security.hasActiveSensorsInSensorGroup = {}
  security.hasActiveSensorsInArea = {}

  // UI
  security.filters = ['alarm', 'sensors']
  security.filterIndex = 0
  security.collapsibleHeaders = {}
  security.alarmTiles = []
  security.disarmTile = null
  security.systemUiIcon = ICONS['shield']
  security.systemUiState = ''

  security.selectFilter = selectFilter
  security.toggleHideGroup = toggleHideGroup

  init()

  function init () {
    $scope.$on('$destroy', onDestroy)

    BasSecurity.getAlarmSystems()
      .then(() => Promise.all([
        setHasActiveSensorsPerSensorGroup(),
        setHasActiveSensorsPerArea()
      ]))
      .then(() => createCollapsibleHeaders())
      .catch(onError)

    _listeners.push($rootScope.$on(
      SECURITY.EVT_SENSOR_STATE_CHANGED,
      () => {
        setHasActiveSensorsPerSensorGroup()
        setHasActiveSensorsPerArea()
      }
    ))

    _listeners.push($rootScope.$on(
      SECURITY.EVT_SYSTEM_STATE_CHANGED,
      _updateSystemState
    ))
  }

  /**
   * @param {number} index
   */
  function selectFilter (index) {
    if (security.filters[index]) {
      security.filterIndex = index
    }
  }

  function createCollapsibleHeaders () {
    const alarmAreas = security.state.alarmSystem.alarmAreas

    alarmAreas.forEach((area) => {
      const sensorGroups = area.sensorGroups

      sensorGroups.forEach((group) => {
        const groupId = group.uuid

        if (!Object.keys(security.collapsibleHeaders).includes(groupId)) {
          security.collapsibleHeaders[groupId] = {
            isCollapsed: false
          }
        }
      })
    })
  }

  /**
   * @param {string} key
   */
  function toggleHideGroup (key) {

    const header = security.collapsibleHeaders[key]

    header.isCollapsed = !header.isCollapsed
  }

  function setHasActiveSensorsPerSensorGroup () {
    const activeStates = [
      SECURITY.ACTIVE_STATE_OPEN,
      SECURITY.ACTIVE_STATE_TRIGGERED,
      SECURITY.ACTIVE_STATE_ACTIVATED
    ]

    security.hasActiveSensorsInSensorGroup =
      security.state.alarmSystem.alarmAreas.reduce((hasMap, area) => {
        area.sensorGroups.forEach(group => {
          hasMap[group.uuid] = group.sensors.some(
            sensor => activeStates.includes(sensor.state)
          )
        })
        return hasMap
      }, {})

    $rootScope.$applyAsync()
  }

  function setHasActiveSensorsPerArea () {
    const activeStates = [
      SECURITY.ACTIVE_STATE_OPEN,
      SECURITY.ACTIVE_STATE_TRIGGERED,
      SECURITY.ACTIVE_STATE_ACTIVATED
    ]

    security.hasActiveSensorsInArea = security.state.alarmSystem.alarmAreas
      .reduce((hasMap, area) => {
        hasMap[area.uuid] = area.sensors.some(
          (sensor) => activeStates.includes(sensor.state)
        )
        return hasMap
      }, {})

    $rootScope.$applyAsync()
  }

  /**
   * Alarm is undefined when disarming alarm system
   *
   * @param {TBasAlarm | undefined} alarm
   */
  function showInputModal (alarm) {
    const alarmSystem = security.state.alarmSystem
    const countdown = alarmSystem.state === SECURITY.BAS_SYSTEM_STATE_DISARMED
      ? alarmSystem.armDelaySeconds
      : 0

    BasSecurity.showInputModal(
      alarm !== undefined ? alarm : null,
      countdown
    )
  }

  function _updateSystemState () {
    security.alarmTiles = []
    security.state = BasSecurity.get()
    const alarmSystem = security.state.alarmSystem

    alarmSystem.alarms.map(
      (alarm) => security.alarmTiles.push(
        new BasSecurityAlarmTile(alarm)
      )
    )

    _syncUiSystemTile()
    $rootScope.$applyAsync()
  }

  function _syncUiSystemTile () {
    const alarmSystem = security.state.alarmSystem

    switch (alarmSystem.state) {
      case SECURITY.BAS_SYSTEM_STATE_ARMED:
        security.systemUiIcon = ICONS[SECURITY.BAS_ALARM_ICON_ARMED]
        security.systemUiState = BasUtilities.translate('alarm_system_armed')
        break

      case SECURITY.BAS_SYSTEM_STATE_TRIGGERED:
        security.systemUiIcon = ICONS[SECURITY.BAS_ALARM_ICON_TRIGGERED]
        security.systemUiState =
          BasUtilities.translate('alarm_system_triggered')
        break

      default:
        security.systemUiIcon = ICONS[SECURITY.BAS_ALARM_ICON_DISARMED]
        security.systemUiState =
          BasUtilities.translate('alarm_system_disarmed')
        break
    }

    // Create a disarm tile for all states except disarmed or unknown
    if (
      alarmSystem.state !== SECURITY.BAS_SYSTEM_STATE_DISARMED &&
      alarmSystem.state !== SECURITY.BAS_SYSTEM_STATE_UNKNOWN &&
      security.state.canDisarm
    ) {
      security.disarmTile = new BasSecurityAlarmTile({
        translationKey: SECURITY.BAS_ALARM_TYPE_DISARM
      })
    }
  }

  function onError (error) {

    Logger.error('BasSecurity - onError', error)
  }

  /**
   * @private
   */
  function onDestroy () {
    BasSecurity.clearState()
    BasUtil.executeArray(_listeners)
    _listeners = []
  }
}
